"use client"

import * as React from "react"

import { cn } from "@/lib/utils"

type InputOTPContextValue = {
  value: string
  maxLength: number
  setCharAt: (index: number, char: string) => void
  fillFrom: (index: number, chunk: string) => void
  focusAt: (index: number) => void
  registerRef: (index: number, node: HTMLInputElement | null) => void
}

const InputOTPContext = React.createContext<InputOTPContextValue | null>(null)

function useInputOTPContext() {
  const context = React.useContext(InputOTPContext)
  if (!context) {
    throw new Error("InputOTP components must be used inside <InputOTP />")
  }
  return context
}

export interface InputOTPProps extends Omit<React.ComponentProps<"div">, "onChange"> {
  value: string
  onChange: (value: string) => void
  maxLength?: number
}

function InputOTP({
  className,
  value,
  onChange,
  maxLength = 6,
  children,
  ...props
}: InputOTPProps) {
  const refs = React.useRef<Array<HTMLInputElement | null>>([])

  const normalizedValue = React.useMemo(
    () => value.slice(0, maxLength),
    [maxLength, value],
  )

  const updateValue = React.useCallback(
    (updater: (chars: string[]) => void) => {
      const chars = Array.from({ length: maxLength }, (_, i) => normalizedValue[i] ?? "")
      updater(chars)
      onChange(chars.join("").trim())
    },
    [maxLength, normalizedValue, onChange],
  )

  const contextValue = React.useMemo<InputOTPContextValue>(
    () => ({
      value: normalizedValue,
      maxLength,
      setCharAt: (index, char) => {
        updateValue((chars) => {
          chars[index] = char
        })
      },
      fillFrom: (index, chunk) => {
        updateValue((chars) => {
          const available = Math.max(0, maxLength - index)
          const slice = chunk.slice(0, available)
          slice.split("").forEach((char, offset) => {
            chars[index + offset] = char
          })
        })
      },
      focusAt: (index) => {
        refs.current[index]?.focus()
      },
      registerRef: (index, node) => {
        refs.current[index] = node
      },
    }),
    [maxLength, normalizedValue, updateValue],
  )

  return (
    <InputOTPContext.Provider value={contextValue}>
      <div
        data-slot="input-otp"
        className={cn("flex items-center justify-center gap-2", className)}
        {...props}
      >
        {children}
      </div>
    </InputOTPContext.Provider>
  )
}

function InputOTPGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="input-otp-group"
      className={cn("flex items-center gap-2", className)}
      {...props}
    />
  )
}

interface InputOTPSlotProps extends Omit<React.ComponentProps<"input">, "onChange"> {
  index: number
}

function InputOTPSlot({ className, index, ...props }: InputOTPSlotProps) {
  const { value, maxLength, setCharAt, fillFrom, focusAt, registerRef } = useInputOTPContext()
  const char = value[index] ?? ""

  return (
    <input
      ref={(node) => registerRef(index, node)}
      data-slot="input-otp-slot"
      inputMode="numeric"
      autoComplete="one-time-code"
      className={cn(
        "h-10 w-10 rounded-md border border-input bg-transparent text-center text-base shadow-sm outline-none transition focus-visible:ring-1 focus-visible:ring-ring md:text-sm",
        className,
      )}
      value={char}
      onChange={(event) => {
        const nextChar = event.target.value.slice(-1)
        setCharAt(index, nextChar)
        if (nextChar && index < maxLength - 1) {
          focusAt(index + 1)
        }
      }}
      onKeyDown={(event) => {
        if (event.key === "Backspace" && !char && index > 0) {
          focusAt(index - 1)
        }
      }}
      onPaste={(event) => {
        event.preventDefault()
        const pasted = event.clipboardData.getData("text")
        if (!pasted) return

        fillFrom(index, pasted)
        const focusIndex = Math.min(index + pasted.length, maxLength - 1)
        focusAt(focusIndex)
      }}
      {...props}
    />
  )
}

export { InputOTP, InputOTPGroup, InputOTPSlot }

